<template>
  <div class="form">
    <h6 class="form__title">{{ t('login.title') }}</h6>
    <ui-input
        :type="'email'"
        :label="t('fields.email')"
        :rules="[required]"
        v-model="email"
    />
    <ui-input
        :type="'password'"
        :label="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
    />
    <div class="form__captcha">
      <Checkbox v-model="captcha" />
    </div>
    <ui-button
        class="form__button"
        :isDisabled="!isFormValid"
        @click="login"
    >
      {{ t('buttons.login') }}
    </ui-button>
    <div class="form__bottom">
      <ui-checkbox
          v-model="isRemember"
      >
        {{ t('checkboxes.remember') }}
      </ui-checkbox>
      <router-link :to="translations.i18nRoute({ name: 'reset' })" class="form__lost">{{ t('login.lost') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import {computed, ref} from "vue";
import UiButton from "@/components/ui/ui-button.vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import translation from "@/core/helpers/translations.js";
import {Wishlist} from "@/core/api/wishlist.js";
import {Orders} from "@/core/api/orders.js";
import translations from "@/core/helpers/translations.js";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useRouter} from "vue-router";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import { Checkbox } from 'vue-recaptcha';

const {t} = useI18n()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const router = useRouter()

const email = ref('')
const password = ref('')
const isRemember = ref(false)
const captcha = ref()

const isLoading = ref(false)

const isFormValid = computed(() => {
  return (
    isEmail(email.value) === true &&
    required(password.value) === true &&
    captcha.value
  )
})

const login = async () => {
  isLoading.value = true

  if (isRemember.value) {
    localStorage.setItem('ngs-remember', 'remember')
  }

  const res = await Auth.login(email.value, password.value)

  if (!res.errors) {
    authStore.setUser({
      user: res.data.obtainJwtToken.user,
      accessToken: res.data.obtainJwtToken.accessToken
    })

    if (res.data.obtainJwtToken.user.language.toLowerCase() !== translation.currentLocale) {
      translations.switchLanguage(res.data.obtainJwtToken.user.language)
    }

    ElNotification({
      message: t('popup.login.text'),
      type: 'success'
    })

    if (localStorage.getItem('ngs-remember')) {
      localStorage.setItem('ngs-refresh', res.data.obtainJwtToken.refreshToken)
    }

    await router.push({
      name: 'home',
      params: {
        locale: localStorage.getItem('ngs-user-locale') || 'en-gb'
      }
    })

    localStorage.setItem('ngs-refresh', res.data.obtainJwtToken.refreshToken)

    const currentOrder = await Orders.getOrders("PENDING", res.data.obtainJwtToken.accessToken, res.data.obtainJwtToken.user.email)
    cartStore.setCurrentOrders(currentOrder.data.orders.edges[0].node)

    const wishlistRes = await Wishlist.getWishlist(res.data.obtainJwtToken.accessToken)
    wishlistStore.wishlist = wishlistRes.data.wishlists.edges[0].node
  } else {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-block: 10px;
  padding-right: 20px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);

  &__title {
    color: #2F2F2F;
    font-size: 22px;
    font-weight: 600;
    line-height: 30.8px;
    text-transform: uppercase;
  }

  &__link {
    transition: 0.2s;

    @include hover {
      color: #6c6c6c;
    }
  }

  &__button {
    padding-block: 12px;
    font-size: 14px;
  }

  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__lost {
    position: relative;
    color: #FF6162;
    font-size: 14px;
    font-weight: 400;
    line-height: 22.4px;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      height: 2px;
      width: 0;
      transition: all .3s ease;
      background-color: #FF6162;
    }

    @include hover {
      &::after {
        width: 100%;
      }
    }
  }
}

@media (max-width: 1100px) {
  .form {
    width: 400px;
    align-items: center;
    padding-right: 0;
    padding-bottom: 30px;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &__bottom {
      width: 100%;
    }
  }
}

@media (max-width: 500px) {
  .form {
    width: 100%;
  }
}
</style>